import React, { useEffect, useState } from 'react';
import { Menu, X, Instagram, Facebook } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT } from '../mock';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';

const NAV = [
  { key: 'home', href: '#home' },
  { key: 'rooms', href: '#camere' },
  { key: 'experiences', href: '#cosa-fare' },
  { key: 'contact', href: '#contatti' },
];

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const dark = scrolled;
  const textCol = dark ? 'text-[#2A2420]' : 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark ? 'bg-[#F6EFE3]/95 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.06)] py-3' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
        <button onClick={() => scrollTo('#home')} className="flex flex-col items-start leading-none group">
          <span className={`font-display text-2xl md:text-[26px] font-600 tracking-wide transition-colors ${textCol}`}>
            la Bellavita
          </span>
          <span className={`font-body text-[10px] md:text-[11px] tracking-[0.4em] uppercase transition-colors ${dark ? 'text-[#B5603A]' : 'text-[#E7DAC4]'}`}>
            rooms
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <button
              key={item.key}
              onClick={() => scrollTo(item.href)}
              className={`relative font-body text-[15px] tracking-wide transition-colors after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[1.5px] after:w-0 after:bg-[#B5603A] after:transition-all after:duration-300 hover:after:w-full ${textCol} hover:text-[#B5603A]`}
            >
              {t(`nav.${item.key}`)}
            </button>
          ))}

          <div className={`flex items-center gap-1 text-[13px] font-body tracking-wider ${textCol}`}>
            <button
              onClick={() => lang !== 'it' && toggle()}
              className={`px-1 transition-colors ${lang === 'it' ? 'text-[#B5603A] font-500' : 'opacity-60 hover:opacity-100'}`}
            >IT</button>
            <span className="opacity-40">/</span>
            <button
              onClick={() => lang !== 'en' && toggle()}
              className={`px-1 transition-colors ${lang === 'en' ? 'text-[#B5603A] font-500' : 'opacity-60 hover:opacity-100'}`}
            >EN</button>
          </div>

          <button
            onClick={() => scrollTo('#contatti')}
            className="font-body text-[14px] tracking-wide px-6 py-2.5 rounded-full bg-[#B5603A] text-white hover:bg-[#8F4A2C] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            {t('nav.book')}
          </button>
        </nav>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-4">
          <div className={`flex items-center gap-1 text-[13px] font-body ${textCol}`}>
            <button onClick={() => lang !== 'it' && toggle()} className={lang === 'it' ? 'text-[#B5603A] font-500' : 'opacity-60'}>IT</button>
            <span className="opacity-40">/</span>
            <button onClick={() => lang !== 'en' && toggle()} className={lang === 'en' ? 'text-[#B5603A] font-500' : 'opacity-60'}>EN</button>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className={textCol} aria-label="menu">
                <Menu className="w-7 h-7" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#F6EFE3] border-none w-[80%] sm:w-[360px] p-0">
              <div className="flex flex-col h-full">
                <div className="px-7 pt-8 pb-6 border-b border-[#E7DAC4]">
                  <span className="font-display text-3xl text-[#2A2420]">la Bellavita</span>
                  <p className="font-body text-[11px] tracking-[0.4em] uppercase text-[#B5603A] mt-1">rooms</p>
                </div>
                <nav className="flex flex-col px-7 py-6 gap-1">
                  {NAV.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => scrollTo(item.href)}
                      className="text-left font-display text-2xl text-[#2A2420] py-3 border-b border-[#E7DAC4]/60 hover:text-[#B5603A] transition-colors"
                    >
                      {t(`nav.${item.key}`)}
                    </button>
                  ))}
                </nav>
                <button
                  onClick={() => scrollTo('#contatti')}
                  className="mx-7 mt-auto font-body tracking-wide px-6 py-3.5 rounded-full bg-[#B5603A] text-white hover:bg-[#8F4A2C] transition-colors"
                >
                  {t('nav.book')}
                </button>
                <div className="flex items-center justify-center gap-4 mx-7 mt-6 mb-8">
                  <a
                    href={CONTACT.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-11 h-11 rounded-full border border-[#E7DAC4] flex items-center justify-center text-[#2A2420] hover:bg-[#B5603A] hover:border-[#B5603A] hover:text-white transition-all duration-300"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href={CONTACT.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-11 h-11 rounded-full border border-[#E7DAC4] flex items-center justify-center text-[#2A2420] hover:bg-[#B5603A] hover:border-[#B5603A] hover:text-white transition-all duration-300"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
